import { ColumnSizing } from '../tables-context';
import { MRT_ColumnDef } from 'material-react-table';
import React, { FC } from 'react';
import { TableCell } from '../../../components/table/TableCell';
import { StatusBadge } from '../../../components/status/StatusBadge';
import { DateComponent } from '../../../components/DateComponent';
import { getListItemValue } from '../../../utils/utilities';
import { useMerchants } from '../../merchnts/merchants-context';
import { CopyComponent } from '../../../components/CopyComponent';
import { RELATED_ENTITY_TYPE, RelatedEntityNameLink } from '../../../components/merchants/RelatedEntityNameLink';
import { WhisperComponent } from '../../../components/developers/page-components/WhisperComponent';
import { ContactSpeaker } from './merchant-data';

export interface IPreparedApplicationData {
	id: string;
	applicationName: string;
	industry: string;
	agreeToTerms: string;
	createdOn: string;
	contactName: string;
	email: string;
	phone: string;
	status: string;
	operationsStatus: string;
	riskStatus: string;
	partnerId: string;
}

export enum ApplicationTableAccessorKey {
	id = 'id',
	applicationName = 'applicationName',
	industry = 'industry',
	submittedOn = 'agreeToTerms',
	createdOn = 'createdOn',
	contactName = 'contactName',
	status = 'status',
	operationsStatus = 'operationsStatus',
	riskStatus = 'riskStatus',
	partnerId = 'partnerId',
}

export const defaultApplicationColumnSizing: ColumnSizing = {
	[ApplicationTableAccessorKey.id]: 120,
	[ApplicationTableAccessorKey.partnerId]: 100,
	[ApplicationTableAccessorKey.applicationName]: 100,
	[ApplicationTableAccessorKey.industry]: 70,
	[ApplicationTableAccessorKey.submittedOn]: 70,
	[ApplicationTableAccessorKey.createdOn]: 70,
	[ApplicationTableAccessorKey.contactName]: 120,
	[ApplicationTableAccessorKey.status]: 70,
	[ApplicationTableAccessorKey.operationsStatus]: 70,
	[ApplicationTableAccessorKey.riskStatus]: 70,
};

export const defaultApplicationsColumns: MRT_ColumnDef<IPreparedApplicationData>[] = [
	{
		accessorKey: ApplicationTableAccessorKey.partnerId,
		header: 'Partner',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Partner</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black fs-7"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<RelatedEntityNameLink id={renderedCellValue} type={RELATED_ENTITY_TYPE.PARTNER} />
			</TableCell>
		)
	},
	{
		accessorKey: ApplicationTableAccessorKey.status,
		header: 'Status',
		// enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Status</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black fw-bolder fs-7"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<StatusBadge status={renderedCellValue}/>
			</TableCell>
		)
	},
	{
		accessorKey: ApplicationTableAccessorKey.applicationName,
		header: 'Name',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Name</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black fs-7"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				{renderedCellValue}
			</TableCell>
		)
	},
	{
		accessorKey: ApplicationTableAccessorKey.riskStatus,
		header: 'Risk status',
		// enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Risk status</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black fw-bolder fs-7"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<ApplicationListStatusBadge status={renderedCellValue} isRisk/>
			</TableCell>
		)
	},
	{
		accessorKey: ApplicationTableAccessorKey.operationsStatus,
		header: 'Operations status',
		// enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Operations status</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black fw-bolder fs-7"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<ApplicationListStatusBadge status={renderedCellValue} />
			</TableCell>
		)
	},
	{
		accessorKey: ApplicationTableAccessorKey.submittedOn,
		header: 'Submitted date',
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Submitted date</span>,
		// enableSorting: false,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				{
					renderedCellValue
						? (
							<DateComponent
								value={renderedCellValue}
								isTime
							/>
						) : '-'
				}
			</TableCell>
		)
	},
	{
		accessorKey: ApplicationTableAccessorKey.createdOn,
		header: 'Created date',
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Created date</span>,
		// enableSorting: false,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				{
					renderedCellValue
						? (
							<DateComponent
								value={renderedCellValue}
								isTime
							/>
						) : '-'
				}
			</TableCell>
		)
	},
	{
		accessorKey: ApplicationTableAccessorKey.contactName,
		header: 'Contact name',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Contact name</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => {
			const email = row?.original?.email;
			const phone = row?.original?.phone;

			return (
				<TableCell
					additionalClass="main-font-family text-black fs-7"
					density={table?.options?.state?.density}
					isTextOverflow={true}
				>
					<WhisperComponent
						whisperText={ContactSpeaker({ contactName: renderedCellValue, email, phone })}
						mainText={renderedCellValue}
						trigger="click"
						additionalClass="cursor-pointer fc-text-overflow d-block text-decoration-underline"
					/>
				</TableCell>
			);
		}
	},
	{
		accessorKey: ApplicationTableAccessorKey.id,
		header: 'Id',
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Id</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => {
			return (
				<TableCell
					additionalClass="d-flex align-items-center main-font-family text-black fs-7"
					density={table?.options?.state?.density}
					isTextOverflow={true}
				>
					<CopyComponent value={renderedCellValue} title="Application Id"/>
						<div className="fw-bolder ms-2 fc-text-overflow">
							{renderedCellValue}
						</div>
				</ TableCell>
			);
		},
	},
];

const ApplicationListStatusBadge: FC<{ status: string, isRisk?: boolean }> = ({ status, isRisk }) => {
	const { merchantState: { merchantsLists }} = useMerchants();

	const list = isRisk ? merchantsLists?.applicationRiskStatuses : merchantsLists?.applicationOperationsStatuses;

	const statusName = getListItemValue(status, list);

	return (
		<>
			{
				statusName && (
					<StatusBadge status={statusName} />
				)
			}
		</>
	);
};
